/**
 * BSA — Page "Mes Analyses"
 * Renders analysis history cards with parameters, scores, recommendations.
 * Depends on bsa-api.js and bsa-pages.css.
 */

(function () {
  "use strict";

  // ─── Reference values (same as BSA main) ──────────────────────

  const VALUES = {
    hardness: [0, 100, 250, 500, 1000],
    fc: [0, 0.5, 1, 3, 5, 10],
    br: [0, 1, 2, 6, 10, 20],
    tc: [0, 0.5, 1, 3, 5, 10],
    cya: [0, 40, 100, 150, 240],
    alk: [0, 40, 80, 120, 180, 240],
    ph: [6.2, 6.8, 7.2, 7.8, 8.4, 9.0],
  };

  const TARGETS = {
    spa: {
      hardness: { min: 175, max: 400 },
      alk: { min: 80, max: 160 },
      ph: { min: 7.2, max: 7.8 },
      fc: { min: 3, max: 6 },
      br: { min: 3, max: 6 },
    },
    pool: {
      hardness: { min: 175, max: 400 },
      alk: { min: 80, max: 160 },
      ph: { min: 7.2, max: 7.8 },
      fc: { min: 1, max: 3 },
      br: { min: 1, max: 3 },
      cya: { min: 30, max: 50 },
    },
  };

  const PARAM_LABELS = {
    ph: "pH",
    alk: "Alcalin.",
    hardness: "Dureté",
    fc: "Chlore L.",
    br: "Brome",
    tc: "Chlore T.",
    cya: "CYA",
  };

  const INDEX_MAP = {
    ph_index: "ph",
    alcalinite_index: "alk",
    durete_calcique_index: "hardness",
    chlore_libre_index: "fc",
    brome_index: "br",
    chlore_total_index: "tc",
    stabilisant_cya_index: "cya",
  };

  const ETAT_MAP = {
    crystal: { emoji: "💎", label: "Eau cristalline", cls: "crystal" },
    cloudy: { emoji: "🌫️", label: "Eau brouillée", cls: "cloudy" },
    lightalgae: { emoji: "🟡", label: "Eau colorée", cls: "lightalgae" },
    algae: { emoji: "🟢", label: "Algues", cls: "algae" },
  };

  // ─── State ────────────────────────────────────────────────────

  let bassins = [];
  let analyses = [];
  let selectedBassinId = null;
  let hasNextPage = false;
  let endCursor = null;
  let isLoading = false;

  const container = document.getElementById("bsa-analyses-app");
  if (!container) return;

  const customerId = container.dataset.customerId;
  if (!customerId) {
    container.innerHTML = `
      <div class="bsa-login-prompt">
        <h2>Connectez-vous</h2>
        <p>Vous devez être connecté pour voir vos analyses d'eau.</p>
        <a href="/account/login" class="bsa-btn bsa-btn--primary">Se connecter</a>
      </div>
    `;
    return;
  }

  // ─── Init ─────────────────────────────────────────────────────

  async function init() {
    showLoading();
    try {
      // Auto-select bassin from URL param (e.g. ?bassin=gid://...)
      const urlParams = new URLSearchParams(window.location.search);
      const bassinParam = urlParams.get("bassin");
      if (bassinParam) selectedBassinId = bassinParam;

      window.BSA_API.init({ customerId });
      const profile = await window.BSA_API.loadCustomerProfile();
      bassins = profile?.bassins || [];
      await loadAnalyses();
    } catch (e) {
      console.error("[BSA] Init error:", e);
      container.innerHTML = '<p style="text-align:center;color:#d63b3b;font-weight:700;">Erreur de chargement. Veuillez réessayer.</p>';
    }
  }

  async function loadAnalyses(append = false) {
    isLoading = true;
    const params = { limit: 10 };
    if (selectedBassinId) params.bassinId = selectedBassinId;
    if (append && endCursor) params.cursor = endCursor;

    const result = await window.BSA_API.getAnalyses(params);
    if (append) {
      analyses = analyses.concat(result.analyses);
    } else {
      analyses = result.analyses;
    }
    hasNextPage = result.pageInfo?.hasNextPage || false;
    endCursor = result.pageInfo?.endCursor || null;
    isLoading = false;
    render();
  }

  // ─── Render ───────────────────────────────────────────────────

  function render() {
    let html = '<div class="bsa-page__centered">';

    html += `
      <div class="bsa-page__header">
        <h1 class="bsa-page__title">Mes analyses d'eau</h1>
        <div class="bsa-page__actions">
          <a href="/pages/water-analyzer" class="bsa-btn bsa-btn--primary">🧪 Nouvelle analyse</a>
        </div>
      </div>
    `;

    // Bassin filter
    if (bassins.length > 1) {
      html += '<div class="bsa-filter">';
      html += `<button class="bsa-filter__pill${!selectedBassinId ? " is-active" : ""}" data-filter-bassin="">Tous</button>`;
      for (const b of bassins) {
        const icon = b.type_d_eau === "spa" ? "🛁" : "🏊";
        const name = b.nom_du_bassin || (b.type_d_eau === "spa" ? "Spa" : "Piscine");
        const active = selectedBassinId === b.id ? " is-active" : "";
        html += `<button class="bsa-filter__pill${active}" data-filter-bassin="${esc(b.id)}">${icon} ${esc(name)}</button>`;
      }
      html += "</div>";
    }

    // CTAs
    html += `
      <div style="text-align:center;margin-bottom:24px;display:flex;gap:12px;justify-content:center;flex-wrap:wrap;">
        <a href="/pages/water-analyzer" class="bsa-btn bsa-btn--primary" style="font-size:17px;padding:14px 28px;">🧪 Analyser mon eau</a>
        <a href="/pages/mes-bassins" class="bsa-btn bsa-btn--secondary" style="font-size:17px;padding:14px 28px;">🏊 Ajouter un bassin</a>
      </div>
    `;

    // Analyses list
    if (analyses.length === 0) {
      html += `
        <div class="bsa-empty">
          <div class="bsa-empty__icon">🧪</div>
          <div class="bsa-empty__title">Aucune analyse</div>
          <div class="bsa-empty__text">Faites votre première analyse d'eau pour suivre la qualité de votre bassin.</div>
        </div>
      `;
    } else {
      // Group by water type
      const spaAnalyses = analyses.filter(a => {
        const b = bassins.find(b => b.id === a.bassin_reference);
        return b?.type_d_eau === "spa";
      });
      const poolAnalyses = analyses.filter(a => {
        const b = bassins.find(b => b.id === a.bassin_reference);
        return !b || b.type_d_eau !== "spa";
      });

      const renderGroup = (title, icon, items) => {
        if (!items.length) return "";
        let groupHtml = `<h3 style="margin:20px 0 12px;font-size:18px;">${icon} ${title}</h3>`;
        const recent = items.slice(0, 3);
        const older = items.slice(3);

        groupHtml += '<div class="bsa-cards">';
        for (const a of recent) groupHtml += renderCard(a);
        groupHtml += "</div>";

        if (older.length) {
          groupHtml += `
            <details style="margin:12px 0 24px;">
              <summary class="bsa-btn bsa-btn--secondary" style="cursor:pointer;text-align:center;width:100%;">
                📋 Analyses précédentes (${older.length})
              </summary>
              <div class="bsa-cards" style="margin-top:12px;">
                ${older.map(a => renderCard(a)).join("")}
              </div>
            </details>
          `;
        }
        return groupHtml;
      };

      html += renderGroup("Spa", "🛁", spaAnalyses);
      html += renderGroup("Piscine", "🏊", poolAnalyses);

      if (hasNextPage) {
        html += `
          <div class="bsa-pagination">
            <button class="bsa-btn bsa-btn--secondary" id="bsa-load-more">Charger plus d'analyses</button>
          </div>
        `;
      }
    }

    html += '</div>'; // .bsa-page__centered

    container.innerHTML = html;
    bindEvents();
  }

  function renderCard(a) {
    const bassin = bassins.find((b) => b.id === a.bassin_reference);
    const bassinName = bassin?.nom_du_bassin || "Bassin";
    const bassinIcon = bassin?.type_d_eau === "spa" ? "🛁" : "🏊";
    const waterType = bassin?.type_d_eau || "pool";

    const date = a.date_d_analyse ? formatDate(a.date_d_analyse) : "—";
    const score = parseInt(a.score_qualite || "0", 10);
    const etat = ETAT_MAP[a.etat_de_l_eau] || ETAT_MAP.crystal;
    const lsi = parseFloat(a.lsi || "0");
    const temp = a.temperature_c ? parseFloat(a.temperature_c) : null;

    const scoreColor = score >= 80 ? "green" : score >= 60 ? "yellow" : "red";
    const lsiZone = window.BSA_LSI ? window.BSA_LSI.getLsiZone(lsi) : null;
    const lsiFormatted = window.BSA_LSI ? window.BSA_LSI.formatLsi(lsi) : lsi.toFixed(2);

    let recos = [];
    try {
      recos = JSON.parse(a.recommandations_json || "[]");
    } catch (_) {}

    // Build params
    const params = [];
    for (const [indexKey, paramKey] of Object.entries(INDEX_MAP)) {
      if (a[indexKey] == null || a[indexKey] === "") continue;
      const index = parseFloat(a[indexKey]);
      const scale = VALUES[paramKey];
      if (!scale) continue;
      const value = interpolateValue(index, scale);
      const target = TARGETS[waterType]?.[paramKey];
      const status = target ? getStatus(value, target) : "ok";
      params.push({
        key: paramKey,
        label: PARAM_LABELS[paramKey],
        value: paramKey === "ph" ? value.toFixed(1) : Math.round(value),
        status,
      });
    }

    // Salt
    if (a.sel_actuel_ppm) {
      params.push({
        key: "salt",
        label: "Sel",
        value: a.sel_actuel_ppm,
        status: "ok",
      });
    }

    let html = `
      <div class="bsa-card" data-analyse-id="${esc(a.id)}">
        <div class="bsa-card__header">
          <div class="bsa-card__bassin-name">${bassinIcon} ${esc(bassinName)}</div>
          <div class="bsa-card__date">${date}</div>
        </div>
        <div class="bsa-card__status">
          <div class="bsa-card__etat bsa-card__etat--${etat.cls}">${etat.emoji} ${etat.label}</div>
          <div class="bsa-card__score">
            <span class="bsa-card__score-label">Score :</span>
            <div class="bsa-card__score-bar">
              <div class="bsa-card__score-fill bsa-card__score-fill--${scoreColor}" style="width:${score}%"></div>
            </div>
            <span class="bsa-card__score-value" style="color:var(--bsa-${scoreColor === "green" ? "success" : scoreColor === "yellow" ? "warning" : "danger"})">${score}%</span>
          </div>
        </div>
        <div class="bsa-card__meta">
    `;

    if (temp != null) {
      html += `<span class="bsa-card__meta-item">🌡️ ${temp}°C</span>`;
    }
    html += `
          <span class="bsa-card__meta-item">LSI : ${lsiFormatted}</span>
          <span class="bsa-lsi-linear-status ${lsiZone ? lsiZone.class : ''}">${lsiZone ? lsiZone.label : '—'}</span>
        </div>
    `;

    // Params grid
    if (params.length > 0) {
      html += '<div class="bsa-card__params">';
      for (const p of params) {
        html += `
          <div class="bsa-param">
            <div class="bsa-param__label">${p.label}</div>
            <div class="bsa-param__value">${p.value}</div>
            <span class="bsa-param__chip bsa-param__chip--${p.status}">${chipLabel(p.status)}</span>
          </div>
        `;
      }
      html += "</div>";
    }

    // Recommendations
    if (recos.length > 0) {
      html += `
        <div class="bsa-card__recos">
          <button class="bsa-card__recos-toggle" data-toggle-recos>
            <span class="arrow">▶</span> Recommandations (${recos.length})
          </button>
          <div class="bsa-card__recos-list">
      `;
      for (const r of recos) {
        const productName = r.product || r.name || "Produit";
        const dose = r.dose || r.quantity || "";
        const handle = r.handle || "";
        html += `
          <div class="bsa-reco">
            <div class="bsa-reco__info">
              <span class="bsa-reco__product">${esc(productName)}</span>
              ${dose ? `<span class="bsa-reco__dose">${esc(dose)}</span>` : ""}
            </div>
            ${handle ? `<button class="bsa-btn bsa-btn--primary bsa-btn--sm bsa-add-to-cart-btn" data-handle="${esc(handle)}">Ajouter au panier</button>` : ""}
          </div>
        `;
      }
      html += `
          </div>
        </div>
      `;
    }

    // Footer
    html += `
        <div class="bsa-card__footer">
          <button class="bsa-btn bsa-btn--secondary bsa-btn--sm" data-view-report="${esc(a.id)}">Voir le rapport complet ↗</button>
        </div>
      </div>
    `;

    return html;
  }

  // ─── Events ───────────────────────────────────────────────────

  function bindEvents() {
    // Filter pills
    container.querySelectorAll("[data-filter-bassin]").forEach((btn) => {
      btn.addEventListener("click", () => {
        selectedBassinId = btn.dataset.filterBassin || null;
        endCursor = null;
        loadAnalyses();
      });
    });

    // Load more
    const loadMoreBtn = document.getElementById("bsa-load-more");
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener("click", () => {
        loadMoreBtn.textContent = "Chargement...";
        loadMoreBtn.disabled = true;
        loadAnalyses(true);
      });
    }

    // Toggle recommendations
    container.querySelectorAll("[data-toggle-recos]").forEach((btn) => {
      btn.addEventListener("click", () => {
        btn.classList.toggle("is-open");
        const list = btn.nextElementSibling;
        if (list) list.classList.toggle("is-open");
      });
    });

    // View report
    container.querySelectorAll("[data-view-report]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const id = btn.dataset.viewReport;
        const analyse = analyses.find((a) => a.id === id);
        if (analyse) openReportModal(analyse);
      });
    });

    // Add to cart buttons
    container.querySelectorAll(".bsa-add-to-cart-btn").forEach((btn) => {
      btn.addEventListener("click", () => handleAddToCart(btn));
    });
  }

  // ─── Report Modal ─────────────────────────────────────────────

  function openReportModal(a) {
    const bassin = bassins.find((b) => b.id === a.bassin_reference);
    const bassinName = bassin?.nom_du_bassin || "Bassin";
    const waterType = bassin?.type_d_eau || "pool";
    const date = a.date_d_analyse ? formatDate(a.date_d_analyse) : "—";
    const score = parseInt(a.score_qualite || "0", 10);
    const etat = ETAT_MAP[a.etat_de_l_eau] || ETAT_MAP.crystal;
    const lsi = parseFloat(a.lsi || "0");
    const lsiZone = window.BSA_LSI ? window.BSA_LSI.getLsiZone(lsi) : null;
    const lsiFormatted = window.BSA_LSI ? window.BSA_LSI.formatLsi(lsi) : lsi.toFixed(2);
    const temp = a.temperature_c ? parseFloat(a.temperature_c) : null;
    const scoreColor = score >= 80 ? "green" : score >= 60 ? "yellow" : "red";

    let recos = [];
    try { recos = JSON.parse(a.recommandations_json || "[]"); } catch (_) {}

    // Build all params
    const params = [];
    for (const [indexKey, paramKey] of Object.entries(INDEX_MAP)) {
      if (a[indexKey] == null || a[indexKey] === "") continue;
      const index = parseFloat(a[indexKey]);
      const scale = VALUES[paramKey];
      if (!scale) continue;
      const value = interpolateValue(index, scale);
      const target = TARGETS[waterType]?.[paramKey];
      const status = target ? getStatus(value, target) : "ok";
      params.push({ key: paramKey, label: PARAM_LABELS[paramKey], value: paramKey === "ph" ? value.toFixed(1) : Math.round(value), status });
    }

    const overlay = document.createElement("div");
    overlay.className = "bsa-modal-overlay";
    overlay.innerHTML = `
      <div class="bsa-modal">
        <button class="bsa-modal__close">✕</button>
        <div class="bsa-modal__title">Rapport d'analyse — ${esc(bassinName)}</div>

        <div class="bsa-modal__section">
          <div style="display:flex;flex-wrap:wrap;gap:12px;align-items:center;margin-bottom:16px;">
            <div class="bsa-card__etat bsa-card__etat--${etat.cls}">${etat.emoji} ${etat.label}</div>
            <span style="font-weight:700;color:#64748b;">${date}</span>
            ${temp != null ? `<span style="font-weight:700;color:#64748b;">🌡️ ${temp}°C</span>` : ""}
          </div>
          <div style="display:flex;align-items:center;gap:12px;margin-bottom:12px;">
            <span style="font-weight:800;">Score :</span>
            <div class="bsa-card__score-bar" style="flex:1;">
              <div class="bsa-card__score-fill bsa-card__score-fill--${scoreColor}" style="width:${score}%"></div>
            </div>
            <span class="bsa-card__score-value" style="color:var(--bsa-${scoreColor === "green" ? "success" : scoreColor === "yellow" ? "warning" : "danger"})">${score}%</span>
          </div>
          <div style="display:flex;align-items:center;gap:8px;">
            <span style="font-weight:800;">LSI : ${lsiFormatted}</span>
            <span class="bsa-lsi-linear-status ${lsiZone ? lsiZone.class : ''}">${lsiZone ? lsiZone.label : '—'}</span>
          </div>
        </div>

        <div class="bsa-modal__section">
          <div class="bsa-modal__section-title">Paramètres</div>
          <div class="bsa-card__params">
            ${params.map((p) => `
              <div class="bsa-param">
                <div class="bsa-param__label">${p.label}</div>
                <div class="bsa-param__value">${p.value}</div>
                <span class="bsa-param__chip bsa-param__chip--${p.status}">${chipLabel(p.status)}</span>
              </div>
            `).join("")}
          </div>
        </div>

        ${recos.length > 0 ? `
          <div class="bsa-modal__section">
            <div class="bsa-modal__section-title">Recommandations</div>
            ${recos.map((r) => {
              const name = r.product || r.name || "Produit";
              const dose = r.dose || r.quantity || "";
              const handle = r.handle || "";
              return `
                <div class="bsa-reco">
                  <div class="bsa-reco__info">
                    <span class="bsa-reco__product">${esc(name)}</span>
                    ${dose ? `<span class="bsa-reco__dose">${esc(dose)}</span>` : ""}
                  </div>
                  ${handle ? `<button class="bsa-btn bsa-btn--primary bsa-btn--sm bsa-add-to-cart-btn" data-handle="${esc(handle)}">Ajouter au panier</button>` : ""}
                </div>
              `;
            }).join("")}
          </div>
        ` : ""}

        <div style="display:flex;gap:10px;flex-wrap:wrap;margin-top:20px;">
          <button class="bsa-btn bsa-btn--secondary" onclick="window.print()">🖨️ Imprimer</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add("is-open"));

    // Close
    overlay.querySelector(".bsa-modal__close").addEventListener("click", () => closeModal(overlay));
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) closeModal(overlay);
    });

    // Cart buttons in modal
    overlay.querySelectorAll(".bsa-add-to-cart-btn").forEach((btn) => {
      btn.addEventListener("click", () => handleAddToCart(btn));
    });
  }

  function closeModal(overlay) {
    overlay.classList.remove("is-open");
    setTimeout(() => overlay.remove(), 250);
  }

  // ─── Add to Cart (with variant detection) ─────────────────────

  async function handleAddToCart(btn) {
    const handle = btn.dataset.handle;
    if (!handle) return;

    btn.disabled = true;
    btn.textContent = "Ajout...";

    try {
      const product = await window.BSA_API.getProductByHandle(handle);
      if (!product) {
        btn.textContent = "En boutique";
        btn.disabled = true;
        return;
      }

      if (product.variants.length === 1) {
        await window.BSA_API.addToCart(product.variants[0].id, 1);
        btn.textContent = "✓ Ajouté";
        btn.classList.add("bsa-btn--cart-success");
        setTimeout(() => {
          btn.textContent = "Ajouter au panier";
          btn.classList.remove("bsa-btn--cart-success");
          btn.disabled = false;
        }, 1500);
      } else {
        btn.textContent = "Ajouter au panier";
        btn.disabled = false;
        openVariantModal(product, btn.closest(".bsa-reco")?.querySelector(".bsa-reco__dose")?.textContent || "");
      }
    } catch (e) {
      btn.textContent = "Erreur";
      btn.classList.add("bsa-btn--danger");
      setTimeout(() => {
        btn.textContent = "Ajouter au panier";
        btn.classList.remove("bsa-btn--danger");
        btn.disabled = false;
      }, 2000);
    }
  }

  function openVariantModal(product, dose) {
    const overlay = document.createElement("div");
    overlay.className = "bsa-modal-overlay";

    const imageUrl = product.featured_image || product.images?.[0] || "";
    let selectedVariantId = null;

    overlay.innerHTML = `
      <div class="bsa-modal bsa-variant-modal">
        <button class="bsa-modal__close">✕</button>
        <div class="bsa-modal__title">${esc(product.title)}</div>
        ${imageUrl ? `<img src="${imageUrl}" alt="${esc(product.title)}" class="bsa-variant-modal__image">` : ""}
        <div style="font-weight:800;font-size:14px;margin-bottom:8px;">Choisir une taille :</div>
        <div class="bsa-variant-pills">
          ${product.variants.map((v) => `
            <button class="bsa-variant-pill" data-variant-id="${v.id}">
              ${esc(v.title)} — ${formatPrice(v.price)}
            </button>
          `).join("")}
        </div>
        ${dose ? `<div class="bsa-variant-dose">Dose recommandée : ${esc(dose)}</div>` : ""}
        <button class="bsa-btn bsa-btn--primary" id="bsa-variant-add" disabled style="width:100%;">Ajouter au panier</button>
      </div>
    `;

    document.body.appendChild(overlay);
    requestAnimationFrame(() => overlay.classList.add("is-open"));

    // Variant selection
    overlay.querySelectorAll(".bsa-variant-pill").forEach((pill) => {
      pill.addEventListener("click", () => {
        overlay.querySelectorAll(".bsa-variant-pill").forEach((p) => p.classList.remove("is-active"));
        pill.classList.add("is-active");
        selectedVariantId = parseInt(pill.dataset.variantId, 10);
        overlay.querySelector("#bsa-variant-add").disabled = false;
      });
    });

    // Add to cart
    overlay.querySelector("#bsa-variant-add").addEventListener("click", async () => {
      if (!selectedVariantId) return;
      const addBtn = overlay.querySelector("#bsa-variant-add");
      addBtn.textContent = "Ajout...";
      addBtn.disabled = true;
      try {
        await window.BSA_API.addToCart(selectedVariantId, 1);
        addBtn.textContent = "✓ Ajouté";
        addBtn.classList.add("bsa-btn--cart-success");
        setTimeout(() => closeModal(overlay), 1200);
      } catch (_) {
        addBtn.textContent = "Erreur — Réessayer";
        addBtn.disabled = false;
      }
    });

    // Close
    overlay.querySelector(".bsa-modal__close").addEventListener("click", () => closeModal(overlay));
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) closeModal(overlay);
    });
  }

  // ─── Helpers ──────────────────────────────────────────────────

  function interpolateValue(index, scale) {
    if (index <= 0) return scale[0];
    const i = Math.floor(index);
    if (i >= scale.length - 1) return scale[scale.length - 1];
    const frac = index - i;
    return scale[i] + frac * (scale[i + 1] - scale[i]);
  }

  function getStatus(value, target) {
    if (value < target.min) return "low";
    if (value > target.max) return "high";
    return "ok";
  }

  function chipLabel(status) {
    if (status === "ok") return "OK";
    if (status === "low") return "BAS";
    return "ÉLEVÉ";
  }

  function formatDate(iso) {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-CA", { day: "numeric", month: "long", year: "numeric" });
  }

  function formatPrice(cents) {
    return (cents / 100).toLocaleString("fr-CA", { style: "currency", currency: "CAD" });
  }

  function esc(str) {
    const div = document.createElement("div");
    div.textContent = str || "";
    return div.innerHTML;
  }

  function showLoading() {
    container.innerHTML = '<div class="bsa-loading"><div class="bsa-spinner"></div></div>';
  }

  // ─── Boot ─────────────────────────────────────────────────────

  init();
})();
